import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Badge } from 'app/models/badge.model';
import { ConventionRegistrationMinorityGroup } from 'app/models/convention-registration-minority-group.model';
import { ConventionRegistrationSectoralMeeting } from 'app/models/convention-registration-sectoral-meeting.model';
import { DelegateDetail } from 'app/models/delegate-detail.model';
import { EventConvention } from 'app/models/event-convention.model';
import { ConventionRegistrationExtension } from 'app/models/extensions/convention-registration-extension.model';
import { LinkGenderConvention } from 'app/models/link-gender-convention.model';
import { LinkHEUConvention } from 'app/models/link-h-e-u-convention.model';
import { LinkLanguageConvention } from 'app/models/link-language-convention.model';
import { LOVConventionRegistrationType } from 'app/models/lov-convention-registration-type.model';
import { LOVRegistrationApprovalStatus } from 'app/models/lov-registration-approval-status.model';
import { Note } from 'app/models/note.model';
import { OtherAttendeeDetail } from 'app/models/other-attendee-detail.model';
import { PaymentFeeType } from 'app/models/payment-fee-type.model';
import { Payment } from 'app/models/payment.model';
import { ProcessedEntityPayment } from 'app/models/processed-entity-payment.model';
import { ProcessedEntity } from 'app/models/processed-entity.model';
import { QRCode } from 'app/models/q-r-code.model';
import { RegistrationContactInfo } from 'app/models/registration-contact-info.model';
import { Signature } from 'app/models/signature.model';
import { MRMValidators } from '@mrm/core';
import { Moment } from 'moment';

export class ConventionRegistration extends ConventionRegistrationExtension {
  conventionEnglishName: string = null;
  conventionFrenchName: string = null;
  localOrgNumber: string = null;
  localOrgName: string = null;
  localOrgCity: string = null;
  localOrgProvinceDBID: number = null;
  credentialNumber: string = null;
  processedEntityTypeDBID?: number = null;
  processedEntityCredentialDBID: number = 0;
  luisEntityContextDBID?: number = null;
  isRegistered: boolean = false;
  isCancelled: boolean = false;
  isConventionAssistFund: boolean = false;
  previousPersonDBID?: number = null;
  secretaryFirstName: string = null;
  secretaryLastName: string = null;
  secretaryEmail: string = null;
  presidentFirstName: string = null;
  presidentLastName: string = null;
  presidentEmail: string = null;
  signatureRecipient: string = null;
  url: string = null;
  languageDBID?: number = null;
  paymentFeeTypes: PaymentFeeType[] = [];
  conventionRegistrationDBID: number = 0;
  conventionDBID: number = 0;
  processedEntityDBID?: number = null;
  personDBID?: number = null;
  registrationApprovalStatusDBID: number = 0;
  conventionRegistrationTypeDBID: number = 0;
  registrationContactInfoDBID: number = 0;
  linkGenderConventionDBID?: number = null;
  linkLanguageConventionDBID: number = 0;
  linkHeuConventionDBID?: number = null;
  luisContactDBID?: number = null;
  signatureDBID?: number = null;
  paperKitRequired: boolean = false;
  attendedDT?: Date = null;
  registerDT?: Date = null;
  doNotEmailFutureCUPECampaigns: boolean = false;
  registrationApprovedDT?: Date = null;
  badgeDBID?: number = null;
  noteDBID?: number = null;
  createdDT: Date = new Date();
  createdBy: string = null;
  modifiedDT: Date = new Date();
  modifiedBy: string = null;
  rowVersion: number = 0;
  secondSignatureDBID?: number = null;
  signatureLastNotifiedDT?: Date = null;
  secondSignatureLastNotifiedDT?: Date = null;
  isOnlineRegistration: boolean = false;
  qrCodeDBID?: number = null;
  badge: Badge = null;
  convention: EventConvention = null;
  conventionRegistrationType: LOVConventionRegistrationType = null;
  linkGenderConvention: LinkGenderConvention = null;
  linkHeuConvention: LinkHEUConvention = null;
  linkLanguageConvention: LinkLanguageConvention = null;
  note: Note = null;
  processedEntity: ProcessedEntity = null;
  qrCode: QRCode = null;
  registrationApprovalStatus: LOVRegistrationApprovalStatus = null;
  registrationContactInfo: RegistrationContactInfo = null;
  secondSignature: Signature = null;
  signature: Signature = null;
  isEquityCredential: boolean = false; // Identifies if Equity credentail (for 2021).
  numberOfAlternateAllowed: number = null; // Number of alternate (for 2021) is now based on member count.
  isVirtual: boolean = false; // Will be used to identify that this registration is part of a virtual convention.
  isRegistrationClosed: boolean = false; // Will be used to identify that registration is closed.
  isPaymentClosed: boolean = false; // Will be used to identify that registration payment is closed.
  paymentDeadline: string; // Will be used to identify that registration payment is closed (based on this value).
  utcOffset: number; // Will be used to identify that registration payment is closed (based on this value).
  defaultContactEmailAddress: string = null; // Will be used for virtual to send email to (unless user overrides and uses AdminEmail)
  conventionRegistrationMinorityGroups: ConventionRegistrationMinorityGroup[] = [];
  conventionRegistrationSectoralMeetings: ConventionRegistrationSectoralMeeting[] = [];
  delegateDetailAlternateConventionRegistrations: DelegateDetail[] = [];
  delegateDetailConventionRegistrations: DelegateDetail[] = [];
  otherAttendeeDetails: OtherAttendeeDetail[] = [];
  payments: Payment[] = [];
  processedEntityPayments: ProcessedEntityPayment[] = [];

  static getForm(disableAll: boolean = false, includeNav: string[] = []): FormGroup {
    const form = new FormGroup({
      conventionEnglishName: new FormControl({ value: null, disabled: disableAll }),
      conventionFrenchName: new FormControl({ value: null, disabled: disableAll }),
      localOrgNumber: new FormControl({ value: null, disabled: disableAll }),
      localOrgName: new FormControl({ value: null, disabled: disableAll }),
      localOrgCity: new FormControl({ value: null, disabled: disableAll }),
      credentialNumber: new FormControl({ value: null, disabled: disableAll }),
      processedEntityTypeDBID: new FormControl({ value: null, disabled: disableAll }),
      processedEntityCredentialDBID: new FormControl({ value: 0, disabled: disableAll }),
      isRegistered: new FormControl({ value: false, disabled: disableAll }),
      isCancelled: new FormControl({ value: false, disabled: disableAll }),
      isConventionAssistFund: new FormControl({ value: false, disabled: disableAll }),
      previousPersonDBID: new FormControl({ value: null, disabled: disableAll }),
      secretaryFirstName: new FormControl({ value: null, disabled: disableAll }),
      secretaryLastName: new FormControl({ value: null, disabled: disableAll }),
      secretaryEmail: new FormControl({ value: null, disabled: disableAll }),
      presidentFirstName: new FormControl({ value: null, disabled: disableAll }),
      presidentLastName: new FormControl({ value: null, disabled: disableAll }),
      presidentEmail: new FormControl({ value: null, disabled: disableAll }),
      signatureRecipient: new FormControl({ value: null, disabled: disableAll }),
      url: new FormControl({ value: null, disabled: disableAll }),
      languageDBID: new FormControl({ value: null, disabled: disableAll }),
      conventionRegistrationDBID: new FormControl({ value: 0, disabled: disableAll }),
      conventionDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      processedEntityDBID: new FormControl({ value: null, disabled: disableAll }),
      personDBID: new FormControl({ value: null, disabled: disableAll }),
      registrationApprovalStatusDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      conventionRegistrationTypeDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      registrationContactInfoDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      linkGenderConventionDBID: new FormControl({ value: null, disabled: disableAll }),
      linkLanguageConventionDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      linkHeuConventionDBID: new FormControl({ value: null, disabled: disableAll }),
      luisContactDBID: new FormControl({ value: null, disabled: disableAll }),
      signatureDBID: new FormControl({ value: null, disabled: disableAll }),
      paperKitRequired: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      attendedDT: new FormControl({ value: null, disabled: disableAll }),
      registerDT: new FormControl({ value: null, disabled: disableAll }),
      doNotEmailFutureCUPECampaigns: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      registrationApprovedDT: new FormControl({ value: null, disabled: disableAll }),
      badgeDBID: new FormControl({ value: null, disabled: disableAll }),
      noteDBID: new FormControl({ value: null, disabled: disableAll }),
      createdDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      createdBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      modifiedDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      modifiedBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      rowVersion: new FormControl({ value: 0, disabled: disableAll }),
      secondSignatureDBID: new FormControl({ value: null, disabled: disableAll }),
      signatureLastNotifiedDT: new FormControl({ value: null, disabled: disableAll }),
      secondSignatureLastNotifiedDT: new FormControl({ value: null, disabled: disableAll }),
      isOnlineRegistration: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      qrCodeDBID: new FormControl({ value: null, disabled: disableAll }),
      isVirtual: new FormControl({ value: false, disabled: disableAll }),
      isRegistrationClosed: new FormControl({ value: false, disabled: disableAll }),
      isPaymentClosed: new FormControl({ value: false, disabled: disableAll }),
      defaultContactEmailAddress: new FormControl({ value: null, disabled: disableAll }),
      paymentFeeTypes: new FormArray([]),
      conventionRegistrationMinorityGroups: new FormArray([]),
      conventionRegistrationSectoralMeetings: new FormArray([]),
      delegateDetailAlternateConventionRegistrations: new FormArray([]),
      delegateDetailConventionRegistrations: new FormArray([]),
      otherAttendeeDetails: new FormArray([]),
      payments: new FormArray([]),
      processedEntityPayments: new FormArray([])
    });

    // Only add navigational properties to the form when needed.
    // If navigational properties are included they MUST NOT be null when patching the form.
    if (includeNav.length > 0) {
      for (const key of includeNav) {
        switch (key.toLowerCase()) {
          case 'badge':
            form.addControl('badge', Badge.getForm(disableAll)); break;
          case 'convention':
            form.addControl('convention', EventConvention.getForm(disableAll)); break;
          case 'conventionregistrationtype':
            form.addControl('conventionRegistrationType', LOVConventionRegistrationType.getForm(disableAll)); break;
          case 'linkgenderconvention':
            form.addControl('linkGenderConvention', LinkGenderConvention.getForm(disableAll)); break;
          case 'linkheuconvention':
            form.addControl('linkHeuConvention', LinkHEUConvention.getForm(disableAll)); break;
          case 'linklanguageconvention':
            form.addControl('linkLanguageConvention', LinkLanguageConvention.getForm(disableAll)); break;
          case 'note':
            form.addControl('note', Note.getForm(disableAll)); break;
          case 'processedentity':
            form.addControl('processedEntity', ProcessedEntity.getForm(disableAll)); break;
          case 'qrcode':
            form.addControl('qrCode', QRCode.getForm(disableAll)); break;
          case 'registrationapprovalstatus':
            form.addControl('registrationApprovalStatus', LOVRegistrationApprovalStatus.getForm(disableAll)); break;
          case 'registrationcontactinfo':
            form.addControl('registrationContactInfo', RegistrationContactInfo.getForm(disableAll)); break;
          case 'secondsignature':
            form.addControl('secondSignature', Signature.getForm(disableAll)); break;
          case 'signature':
            form.addControl('signature', Signature.getForm(disableAll)); break;
          default:
            // tslint:disable-next-line:max-line-length
            throw new Error(`'${key}' is not a property of ConventionRegistration. Please use one of the following navigational property: 'badge' | 'convention' | 'conventionRegistrationType' | 'linkGenderConvention' | 'linkHeuConvention' | 'linkLanguageConvention' | 'note' | 'processedEntity' | 'qrCode' | 'registrationApprovalStatus' | 'registrationContactInfo' | 'secondSignature' | 'signature'`);
        }
      }
    }

    return form;
  }
}